import {setUser} from '@sentry/react'
import universalCookie, {type CookieChangeOptions} from 'universal-cookie'

import {accountInformationAtom, useSetAccountInformation} from '@/auth/hooks/useAccountInformation'
import {useAuthEffect} from '@/auth/hooks/useIsAuthenticated'
import parseAccountInformation from '@/auth/utils/parseAccountInformation'
import {logError} from '@/utils/logger'

const setSentryUserEffect = atomEffect(get => {
  const account = get(accountInformationAtom)
  if (account) {
    setUser({
      id: account.id,
      email: account.email,
      name: account.accountName,
      refCode: account.refCode,
    })
  } else {
    setUser(null)
  }
})

export default memo(function AuthEffects() {
  const router = useRouter()
  const setAccountInformation = useSetAccountInformation()

  useEffect(() => {
    const cookies = new universalCookie()
    const cookieListener = ({name, value, options}: CookieChangeOptions) => {
      if (name !== 'ACCOUNT') return
      const accountBase64 = value as unknown

      const account = (() => {
        try {
          if (
            (options?.maxAge !== undefined && options.maxAge <= 0) ||
            (options?.expires !== undefined && options.expires <= new Date()) ||
            !accountBase64 ||
            typeof accountBase64 !== 'string'
          )
            return

          return parseAccountInformation(accountBase64)
        } catch (e) {
          logError(e)
        }
      })()

      if (!account) {
        setAccountInformation(undefined)
        return
      }

      setAccountInformation(account)
    }

    cookies.addChangeListener(cookieListener)

    return () => {
      cookies.removeChangeListener(cookieListener)
    }
  }, [setAccountInformation])

  useAuthEffect(
    useCallback(
      function invalidateRouterWhenAuthChangeEffect(isAuthenticated: boolean) {
        if (isAuthenticated) {
          void router.invalidate()
        }
      },
      [router],
    ),
  )

  useAtom(setSentryUserEffect)

  return null
})
